import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import DashboardPeriodFilters from './DashboardPeriodFilters';
import DashboardKpiCards from './DashboardKpiCards';
import DashboardStackedChartCard from './DashboardStackedChartCard';
import { useDashboardChartFilters } from '../../hooks/useDashboardChartFilters';
import apiService from '../../services/api';

const LOST_COLOR = '#EBC41D';
const FOUND_COLOR = '#51beee';
const PENDING_COLOR = '#94a3b8';
const CONFIRMED_COLOR = '#22c55e';
const REJECTED_COLOR = '#ef4444';
const RESOLVED_COLOR = '#0ea5e9';
const CARRIER_COLORS = {
  colissimo: '#f59e0b',
  chronopost: '#6366f1',
  mondial_relay: '#e11d48',
  ups: '#854d0e',
};

const DashboardAnalytics: React.FC = () => {
  const {
    filters,
    granularity,
    setGranularity,
    periodMode,
    setPeriodMode,
    presetKey,
    setPresetKey,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    selectedCity,
    setSelectedCity,
    customRangeInvalid,
  } = useDashboardChartFilters();

  const disabled = customRangeInvalid;

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtres
          </Typography>
          <DashboardPeriodFilters
            granularity={granularity}
            onGranularityChange={setGranularity}
            periodMode={periodMode}
            onPeriodModeChange={setPeriodMode}
            presetKey={presetKey}
            onPresetKeyChange={setPresetKey}
            customFrom={customFrom}
            onCustomFromChange={setCustomFrom}
            customTo={customTo}
            onCustomToChange={setCustomTo}
            selectedCity={selectedCity}
            onSelectedCityChange={setSelectedCity}
            customRangeInvalid={customRangeInvalid}
          />
        </CardContent>
      </Card>

      {disabled && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          Corrigez la période personnalisée pour afficher les statistiques.
        </Typography>
      )}

      <DashboardKpiCards filters={filters} disabled={disabled} />

      <DashboardStackedChartCard
        title="Créations d'annonces"
        filters={filters}
        disabled={disabled}
        queryKey="dashboard-announcements-chart"
        queryFn={async (f) => {
          const response = await apiService.getAnnouncementCreationsChart(f);
          return {
            ...response,
            city: response.city ?? null,
            series: response.series.map((point) => ({ ...point })),
          };
        }}
        seriesKeys={[
          { key: 'lost', label: 'Perdu', color: LOST_COLOR },
          { key: 'found', label: 'Trouvé', color: FOUND_COLOR },
        ]}
        totalLabel={(total) => `${total.toLocaleString('fr-FR')} annonce${total > 1 ? 's' : ''}`}
      />

      <DashboardStackedChartCard
        title="Matchings"
        filters={filters}
        disabled={disabled}
        queryKey="dashboard-matchings-chart"
        queryFn={async (f) => {
          const response = await apiService.getDashboardMatchingsChart(f);
          return {
            ...response,
            series: response.series.map((point) => ({ ...point })),
          };
        }}
        seriesKeys={[
          { key: 'pending', label: 'En attente', color: PENDING_COLOR },
          { key: 'confirmed', label: 'Confirmé', color: CONFIRMED_COLOR },
          { key: 'rejected', label: 'Rejeté', color: REJECTED_COLOR },
          { key: 'resolved', label: 'Résolu', color: RESOLVED_COLOR },
        ]}
        totalLabel={(total) => `${total.toLocaleString('fr-FR')} matching${total > 1 ? 's' : ''}`}
      />

      <DashboardStackedChartCard
        title="Livraisons payées"
        filters={filters}
        disabled={disabled}
        queryKey="dashboard-deliveries-chart"
        queryFn={async (f) => {
          const response = await apiService.getDashboardDeliveriesChart(f);
          return {
            ...response,
            series: response.series.map((point) => ({ ...point })),
          };
        }}
        seriesKeys={[
          { key: 'colissimo', label: 'Colissimo', color: CARRIER_COLORS.colissimo },
          { key: 'mondial_relay', label: 'Mondial Relay', color: CARRIER_COLORS.mondial_relay },
          { key: 'chronopost', label: 'Chronopost', color: CARRIER_COLORS.chronopost },
          { key: 'ups', label: 'UPS', color: CARRIER_COLORS.ups },
        ]}
        totalLabel={(total, data) =>
          `${total.toLocaleString('fr-FR')} livraison${total > 1 ? 's' : ''} · ${(data?.revenue_eur ?? 0).toLocaleString('fr-FR')} €`
        }
      />
    </Box>
  );
};

export default DashboardAnalytics;
